/**
 * Unified redirect handler for platform links
 * Handles redirects after login for any declared link from the platform
 */

/**
 * Get the stored redirect URL from localStorage
 * Checks all possible redirect keys in priority order
 */
export const getStoredRedirectUrl = (): string | null => {
    // Priority order: specific redirects first, then general platform redirect
    const redirectKeys = [
      "fanCardRedirectUrl",
      "sharedBookingRedirect",
      "platformRedirect", // General platform redirect for any declared link
    ];
  
    for (const key of redirectKeys) {
      const redirectUrl = localStorage.getItem(key);
      if (redirectUrl) {
        return redirectUrl;
      }
    }
  
    return null;
  };
  
  /**
   * Store a redirect URL in localStorage
   * Uses platformRedirect as the general key for any platform link
   */
  export const storeRedirectUrl = (url: string): void => {
    // Don't store if it's a sign-in URL or already stored
    if (url.includes("/sign-in") || url.includes("/register")) {
      return;
    }
  
    // Check if URL is from our platform (contains our domain or localhost)
    const isPlatformUrl =
      url.includes(window.location.hostname) ||
      url.includes("localhost") ||
      url.includes("taasnet.com");
  
    if (!isPlatformUrl) {
      return;
    }
  
    // Store in general platform redirect key
    const existingRedirect = localStorage.getItem("platformRedirect");
    if (!existingRedirect || existingRedirect !== url) {
      console.log("storeRedirectUrl: Storing platform redirect URL:", url);
      localStorage.setItem("platformRedirect", url);
    }
  };
  
  /**
   * Clear all stored redirect URLs
   */
  export const clearStoredRedirectUrl = (): void => {
    const redirectKeys = [
      "fanCardRedirectUrl",
      "sharedBookingRedirect",
      "platformRedirect",
    ];
  
    redirectKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
  };
  
  /**
   * Get and clear the stored redirect URL (one-time use)
   */
  export const getAndClearRedirectUrl = (): string | null => {
    const redirectUrl = getStoredRedirectUrl();
    if (redirectUrl) {
      clearStoredRedirectUrl();
    }
    return redirectUrl;
  };
  
  /**
   * Check if a URL is a valid platform route
   */
  export const isValidPlatformRoute = (url: string): boolean => {
    // List of valid platform route patterns
    const validRoutes = [
      "/busking-live-stream",
      "/user",
      "/talent",
      "/business",
      "/cards",
      "/taascards",
      "/instant-cards",
      "/flash-cards",
      "/stai",
      "/airbank",
      "/sessions",
      "/live-stream",
      "/coffee-call",
      "/interaction",
      "/stage",
      "/featured-event",
      "/ai-twins",
      "/cards-with-ai-twin",
      "/wallet",
      "/dashboard",
      "/profile",
      "/search",
      "/message",
      "/tickets",
      "/workspace",
      "/posts",
      "/media-gallery",
      "/flips",
    ];
  
    // Check if URL contains any valid route pattern
    return validRoutes.some((route) => url.includes(route));
  };
  